import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Upload, Download } from 'lucide-react';
import { downloadExport } from '../utils/exportUtils';
import { calcInternalDue, internalDueColor, internalDueBadge, DEFAULT_BUFFERS } from '../utils/dueDate';
import api from '../utils/api';
import usePersistedFilter from '../utils/usePersistedFilter';
import { fmtDate, FY_OPTIONS, CURRENT_FY, statusColor, canDelete, canImport } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, StatusBadge, Modal, FormField } from '../components/Table';
import DueDateEditModal from '../components/DueDateEditModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import ConfirmModal from '../components/ConfirmModal';
import ModuleImport from '../components/ModuleImport';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BulkActionBar from '../components/BulkActionBar';
import BulkEmailModal from '../components/BulkEmailModal';
import WhatsAppModal from '../components/WhatsAppModal';

const ROC_FORMS = ['MGT-7', 'MGT-7A', 'AOC-4', 'AOC-4 XBRL', 'ADT-1', 'DIR-3 KYC', 'DPT-3', 'MSME-1', 'BEN-2', 'INC-20A', 'Form 8', 'Form 11', 'Others'];
const ROC_STATUSES = ['Pending', 'In Progress', 'Filed', 'Filed with Penalty'];

export default function ROCPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('compliance');
  const [data, setData] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [fyFilter, setFyFilter] = usePersistedFilter('pms_filter_fy_roc');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dueDateRecord, setDueDateRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canDel = canDelete(user);
  const canImp = canImport(user);
  const [buffers, setBuffers] = useState(DEFAULT_BUFFERS);
  useEffect(() => { api.get('/settings/buffer').then(r => setBuffers(b => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  // Bulk actions (Feature 3)
  const [selected, setSelected] = useState(new Set());
  const [waTargets, setWaTargets] = useState(null);
  const [emailTargets, setEmailTargets] = useState(null);
  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllSel = rows => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  const selRows = () => filtered.filter(r => selected.has(r.id));
  const bulkUpdate = async updates => { try { await api.post('/roc/compliance/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkDelete = async () => { try { await api.post('/roc/compliance/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkTargets = () => { const seen = new Set(); return selRows().filter(r => { if (seen.has(r.client_id)) return false; seen.add(r.client_id); return true; }).map(r => ({ id: r.client_id, client_id: r.client_id, name: r.client_name, whatsapp: r.whatsapp, email: r.client_email })); };

  const load = () => {
    if (tab === 'compliance') {
      const params = {}; if (fyFilter) params.fy = fyFilter;
      if (showArchived) params.show_archived = '1';
      api.get('/roc/compliance', { params }).then(r => setData(r.data));
    } else api.get('/roc/directors').then(r => setDirectors(r.data));
  };

  // Alt+I — open the Add form ready to type
  useEffect(() => {
    const h = () => { setEditing(null); setForm({ fy: CURRENT_FY, status: 'Pending' }); setShowModal(true); };
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  useEffect(() => {
    api.get('/auth/users').then(r => setStaff(r.data));
  }, []);
  useEffect(() => { load(); }, [tab, fyFilter, showArchived]);

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (tab === 'compliance') {
        if (editing) await api.put(`/roc/compliance/${editing.id}`, form);
        else await api.post('/roc/compliance', form);
      } else {
        await api.post('/roc/directors', form);
      }
      toast.success('Saved'); setShowModal(false); setEditing(null); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.error || e.message || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/roc/compliance/${deleteTarget.id}`); toast.success('Record deleted'); setDeleteTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const filtered = data.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.client_name?.toLowerCase().includes(s) || d.client_code?.toLowerCase().includes(s);
  });
  const filteredDirs = directors.filter(d => !search || d.client_name?.toLowerCase().includes(search.toLowerCase()) || d.name?.toLowerCase().includes(search.toLowerCase()));

  const complianceColumns = [
    { key: 'client_name', label: 'Company', render: (v, row) => (
      <div><div className="font-medium text-gray-800 text-sm">{v}</div>
        {row.client_code && <div className="text-xs text-gray-400">{row.client_code}</div>}
      </div>
    )},
    { key: 'form_type', label: 'Form', render: v => <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{v}</span> },
    { key: 'primary_assignee_name', label: 'Assigned To', render: (v, row) => (
      <span className="text-xs text-gray-600">{v || <span className="text-gray-300">—</span>}
        {row.secondary_assignee_name && <span className="text-gray-400"> / {row.secondary_assignee_name}</span>}
      </span>
    )},
    { key: 'effective_due_date', label: 'Due Date', render: (v, row) => {
      if (!v) return '—';
      const diff = (new Date(v) - new Date()) / 86400000;
      return (
        <div className="flex items-center gap-1.5">
          <span className={diff < 0 ? 'text-red-600 font-semibold' : diff <= 14 ? 'text-orange-600 font-semibold' : ''}>{fmtDate(v)}</span>
          {row.govt_extended ? <span className="bg-orange-100 text-orange-600 text-xs px-1 rounded font-medium">Govt Ext</span> : null}
          {row.revised_due_date && !row.govt_extended ? <span className="text-xs text-blue-500">Revised</span> : null}
          <button onClick={e => { e.stopPropagation(); setDueDateRecord(row); }} className="text-gray-300 hover:text-[#f0a500] transition-colors" title="Edit due date">
            <Pencil size={12} />
          </button>
        </div>
      );
    }},
    { key: 'status', label: 'Status', render: (v, row) => (
      <select value={v} onChange={async e => { await api.put(`/roc/compliance/${row.id}`, { ...row, status: e.target.value, filing_date: e.target.value === 'Filed' ? new Date().toISOString().split('T')[0] : row.filing_date }); load(); }}
        className={`text-xs rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColor(v)}`}>
        {ROC_STATUSES.map(s => <option key={s}>{s}</option>)}
      </select>
    )},
    { key: 'filing_date', label: 'Filed On', render: v => fmtDate(v) },
    { key: 'srn', label: 'SRN' },
    { key: 'internal_due_date', label: 'Internal Due', render: (v, row) => v
      ? <span className={internalDueColor(v, row.status)}>{internalDueBadge(v, row.status)} {fmtDate(v)}</span>
      : <span className="text-gray-300">—</span>
    },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex gap-2 items-center">
        <button onClick={() => { setEditing(row); setForm(row); setShowModal(true); }} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
        <button title={row.is_archived ? 'Unarchive' : 'Archive'} onClick={async () => { await api.patch(`/roc/compliance/${row.id}/${row.is_archived ? 'unarchive' : 'archive'}`); toast.success(row.is_archived ? 'Unarchived' : 'Archived'); load(); }} className={`text-xs ${row.is_archived ? 'text-amber-600' : 'text-gray-300 hover:text-amber-600'}`}>{row.is_archived ? '↩' : '📁'}</button>
        {canDel && <button onClick={() => setDeleteTarget(row)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>}
      </div>
    )},
  ];

  const directorColumns = [
    { key: 'client_name', label: 'Company' },
    { key: 'name', label: 'Director/Partner Name' },
    { key: 'din', label: 'DIN' },
    { key: 'appointment_date', label: 'Appointed', render: v => fmtDate(v) },
    { key: 'resignation_date', label: 'Resigned', render: v => fmtDate(v) },
    { key: 'kyc_status', label: 'DIR-3 KYC', render: v => <StatusBadge status={v} /> },
    { key: 'kyc_year', label: 'KYC Year' },
  ];

  return (
    <div>
      <PageHeader title="ROC Compliance" subtitle="MCA filings for Companies & LLPs"
        actions={
          <div className="flex gap-2">
            {tab === 'compliance' && <button onClick={() => downloadExport('/export/roc', { fy: fyFilter }, `KUA_ROC_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>}
            {canImp && tab === 'compliance' && <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Upload size={15} /> Import</button>}
            <button onClick={() => { setEditing(null); setForm({ fy: CURRENT_FY, status: 'Pending' }); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Filing</button>
          </div>
        } />

      <div className="flex gap-1 mb-4">
        {[['compliance', 'ROC Filings'], ['directors', 'Directors / Partners']].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>
        ))}
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder="Search..." />
          {tab === 'compliance' && <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
            <option value=''>All Years</option>{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
          </select>}
          {tab === 'compliance' && <button onClick={() => setShowArchived(a => !a)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>{showArchived ? '📂 Archived' : '📁 Show Archived'}</button>}
        </div>
                <BulkActionBar module="roc" count={selected.size} staff={staff}
          statuses={ROC_STATUSES}
          onAssign={id => bulkUpdate({ primary_assignee_id: id })}
          onStatus={s => bulkUpdate({ status: s })}
          onDelete={bulkDelete}
          onWA={() => setWaTargets(bulkTargets())}
          onEmail={() => setEmailTargets(bulkTargets())}
          onClear={() => setSelected(new Set())} />
        <DataTable columns={tab === 'compliance' ? complianceColumns : directorColumns} data={tab === 'compliance' ? filtered : filteredDirs} selected={tab === 'compliance' ? selected : undefined} onToggle={tab === 'compliance' ? toggleSel : undefined} onToggleAll={toggleAllSel} />
      </div>

      {showModal && (
        <Modal title={tab === 'compliance' ? (editing ? 'Edit ROC Filing' : 'Add ROC Filing') : 'Add Director'} onClose={() => { setShowModal(false); setEditing(null); }}>
          <form onSubmit={handleSave} className="space-y-4">
            {!editing && <FormField label="Company" required>
              <ClientSearchDropdown
                value={form.client_id || ''}
                initialLabel=""
                service="ROC"
                onChange={(id) => setForm(f => ({ ...f, client_id: id }))}
                required
                placeholder="Type to search companies…"
              />
            </FormField>}
            {tab === 'compliance' ? (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="FY"><select value={form.fy||CURRENT_FY} onChange={e => setForm(f => ({...f, fy: e.target.value}))} className="input-field">{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}</select></FormField>
                <FormField label="Form Type"><select value={form.form_type||''} onChange={e => setForm(f => ({...f, form_type: e.target.value}))} className="input-field"><option value="">Select form</option>{ROC_FORMS.map(t => <option key={t}>{t}</option>)}</select></FormField>
                <FormField label="Due Date"><input type="date" value={form.due_date||''} onChange={e => setForm(f => ({...f, due_date: e.target.value, internal_due_date: f.internal_due_date || calcInternalDue(e.target.value, buffers.buffer_roc)}))} className="input-field" /></FormField>
                <FormField label="Internal Due Date">
                  <div className="flex gap-2">
                    <input type="date" value={form.internal_due_date||''} onChange={e => setForm(f => ({...f, internal_due_date: e.target.value}))} className="input-field flex-1" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, internal_due_date: calcInternalDue(f.due_date, buffers.buffer_roc) }))} className="text-xs border border-gray-200 rounded px-2 py-1.5 text-[#1e3a5f] hover:text-[#f0a500] whitespace-nowrap">Auto</button>
                  </div>
                </FormField>
                <FormField label="Status"><select value={form.status||'Pending'} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="input-field">{ROC_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
                <FormField label="Filing Date"><input type="date" value={form.filing_date||''} onChange={e => setForm(f => ({...f, filing_date: e.target.value}))} className="input-field" /></FormField>
                <FormField label="SRN"><input value={form.srn||''} onChange={e => setForm(f => ({...f, srn: e.target.value}))} className="input-field" /></FormField>
                <FormField label="Primary Assignee">
                  <select value={form.primary_assignee_id||''} onChange={e => setForm(f => ({...f, primary_assignee_id: e.target.value}))} className="input-field">
                    <option value="">Not assigned</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Secondary Assignee">
                  <select value={form.secondary_assignee_id||''} onChange={e => setForm(f => ({...f, secondary_assignee_id: e.target.value}))} className="input-field">
                    <option value="">None</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </FormField>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Name" required><input required value={form.name||''} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="input-field" /></FormField>
                <FormField label="DIN"><input value={form.din||''} onChange={e => setForm(f => ({...f, din: e.target.value}))} className="input-field" /></FormField>
                <FormField label="Appointment Date"><input type="date" value={form.appointment_date||''} onChange={e => setForm(f => ({...f, appointment_date: e.target.value}))} className="input-field" /></FormField>
                <FormField label="DIR-3 KYC Status"><select value={form.kyc_status||'Pending'} onChange={e => setForm(f => ({...f, kyc_status: e.target.value}))} className="input-field"><option>Pending</option><option>Filed</option><option>Not Required</option></select></FormField>
                <FormField label="KYC Year"><input value={form.kyc_year||''} onChange={e => setForm(f => ({...f, kyc_year: e.target.value}))} className="input-field" placeholder="2025" /></FormField>
              </div>
            )}
            <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Save</button><button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}

      {dueDateRecord && <DueDateEditModal record={dueDateRecord} tableName="roc_compliance" moduleName="ROC" onClose={() => setDueDateRecord(null)} onSaved={load} />}
      {waTargets && <WhatsAppModal targets={waTargets} defaultMsg={`Dear Client,

This is a message from Krishna Uday & Associates regarding your compliance work.

Regards,
KU Associates`} onClose={() => setWaTargets(null)} />}
      {emailTargets && <BulkEmailModal targets={emailTargets} defaultSubject="Update from Krishna Uday & Associates" onClose={() => setEmailTargets(null)} />}
      {deleteTarget && <ConfirmModal message="Delete this ROC filing? This cannot be undone." onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}
      {showImport && <ModuleImport module="roc" onComplete={load} onClose={() => setShowImport(false)} />}
    </div>
  );
}
